/**
 * <EmployeeAvatar /> — circular avatar showing the employee photo if
 * available, otherwise the first letters of their name on a brand-
 * gradient circle. Reused by the picker dropdown, the master file
 * header, list rows, and approval chain step cards.
 *
 * Photo source resolution (in order):
 *   1. employeeId set → resolve to GET /api/employees/{id}/photo
 *      (the backend streams the bytes; works for both R2 + localfs
 *      blob storage). Cache-busted by `cacheBust` so post-upload
 *      the new photo replaces the browser-cached one immediately.
 *   2. photoUrl set + already HTTP — use it as-is (legacy support).
 *   3. Otherwise show brand-gradient initials.
 */

import { useEffect, useState } from "react";

import { apiBaseUrl } from "../../lib/api";

export function EmployeeAvatar({
  employeeId,
  photoUrl,
  firstName,
  lastName,
  size = 32,
  className = "",
  cacheBust,
}: {
  employeeId?: string | null;
  photoUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  size?: number;
  className?: string;
  cacheBust?: string | number;
}) {
  const initials = (
    (firstName?.[0] ?? "") + (lastName?.[0] ?? "")
  ).toUpperCase() || "?";

  const dim = `${size}px`;
  const fontSize = Math.max(10, Math.floor(size * 0.4));

  // Build src: prefer the backend streaming endpoint for any employee
  // that HAS a photo set (photoUrl truthy as a "photo exists" flag).
  let src: string | null = null;
  if (employeeId && photoUrl) {
    const v = cacheBust !== undefined ? `?v=${encodeURIComponent(String(cacheBust))}` : "";
    src = `${apiBaseUrl}/api/employees/${encodeURIComponent(employeeId)}/photo${v}`;
  } else if (photoUrl && /^https?:\/\//.test(photoUrl)) {
    src = photoUrl;
  }

  // When the streaming endpoint 404s (typical: the blob was lost
  // after a Railway container reset, OR the photo_url points at a
  // stale storage backend) we want to FALL BACK to initials, not
  // leave a blank circle. Track the error in state + re-derive on
  // src change so a fresh upload's URL gets a clean retry.
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [src]);

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={`${firstName ?? ""} ${lastName ?? ""}`.trim() || "employee"}
        style={{ width: dim, height: dim }}
        className={`rounded-full object-cover border border-border-subtle ${className}`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      style={{ width: dim, height: dim, fontSize: `${fontSize}px` }}
      className={`rounded-full bg-brand-gradient text-white font-semibold flex items-center justify-center select-none ${className}`}
      aria-label={`${firstName ?? ""} ${lastName ?? ""}`.trim() || "employee"}
    >
      {initials}
    </div>
  );
}
